'use client';

import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
    Enum_Componentpricingpricetag_Unit,
    RelatedInfo,
} from "@/app/lib/generated/graphql-types";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import LinkEditor from "@/app/features/sites/admin/link-editor/ui/LinkEditor";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import RelatedInfosView from "@/app/features/shared/related-infos/ui/RelatedInfos.public";
import { LinkEditable } from "@/app/features/shared/__to_be_integrated/types/forms/LinkSchema";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { notNull } from "@/app/lib/common/not-null";
import { usePricingEditor } from "@/app/features/sites/pricing/contexts/PricingEditorContext";
import {
    PricingCategoryEditable,
    PricingPriceEditable,
} from "@/app/features/sites/pricing/types/types";
import {
    PricingCategoryFrame,
    PricingPageFrame,
} from "@/app/features/sites/pricing/ui/shared/PricingFrames";

export default function PricingEditable() {
    const { editable, updateEditable, errorForField } = usePricingEditor();

    if (!editable) return <LoadingSpinner />;

    const introImages = editable.intro.image ? [editable.intro.image] : [];
    const titleError = errorForField?.('intro.title') || undefined;
    const subtitleError = errorForField?.('intro.subtitle') || undefined;

    function updateCategory(index: number, partial: Partial<PricingCategoryEditable>) {
        updateEditable({
            categories: editable!.categories.map((category, categoryIndex) => (
                categoryIndex === index ? { ...category, ...partial } : category
            )),
        });
    }

    function updatePrice(categoryIndex: number, priceIndex: number, partial: Partial<PricingPriceEditable>) {
        const category = editable!.categories[categoryIndex];
        const prices = (category.prices ?? []).map((price, index) => (
            index === priceIndex ? { ...price, ...partial } : price
        ));
        updateCategory(categoryIndex, { prices });
    }

    return (
        <PricingPageFrame
            intro={
                <SlideshowIntroV2
                    title={editable.intro.title}
                    subtitle={editable.intro.subtitle}
                    images={introImages}
                    linkContentToAnchor={false}
                    startChildren={(
                        <EditMediaButtonOverlay
                            layout="inline"
                            onImageChange={(image) => {
                                if (image) updateEditable({ intro: { image } });
                            }}
                        />
                    )}
                    content={(
                        <Stack spacing={1} alignItems="center">
                            <EditableTypographyV2
                                label="Titel"
                                required
                                component="h1"
                                variant="h1"
                                value={editable.intro.title}
                                onChange={(title) => updateEditable({ intro: { title } })}
                                error={Boolean(titleError)}
                                helperText={titleError}
                                sx={{
                                    fontSize: 'clamp(2rem, 4.8vw, 4rem)',
                                    lineHeight: 1.1,
                                    textWrap: 'balance',
                                }}
                            />
                            <EditableTypographyV2
                                label="Untertitel"
                                component="h2"
                                variant="subtitle2"
                                value={editable.intro.subtitle ?? ''}
                                onChange={(subtitle) => updateEditable({ intro: { subtitle } })}
                                error={Boolean(subtitleError)}
                                helperText={subtitleError}
                                sx={{
                                    fontSize: 'clamp(1rem, 2.1vw, 1.5rem)',
                                    lineHeight: 1.35,
                                    textWrap: 'pretty',
                                }}
                            />
                        </Stack>
                    )}
                />
            }
            categories={editable.categories.map((category, categoryIndex) => {
                    const headingId = `pricing-category-${category.id ?? categoryIndex}-title`;

                    return (
                        <PricingCategoryFrame
                            key={category.id ?? `new-category-${categoryIndex}`}
                            categoryId={headingId}
                            index={categoryIndex}
                            content={
                                <Stack spacing={4} sx={{ width: '100%' }}>
                                    <EditableTypographyV2
                                        id={headingId}
                                        label="Kategorietitel"
                                        value={category.title}
                                        onChange={(title) => updateCategory(categoryIndex, { title })}
                                        variant="h2"
                                        required
                                        slots={{ root: { sx: { width: '100%' } } }}
                                    />

                                    <Stack
                                        spacing={2}
                                        sx={{
                                            maxHeight: '50svh',
                                            overflowY: 'auto',
                                            pr: 1,
                                        }}
                                    >
                                        {(category.prices ?? []).filter(notNull).map((price, priceIndex) => (
                                            <PriceEditorRow
                                                key={price.id ?? `new-price-${priceIndex}`}
                                                price={price}
                                                onChange={(partial) => updatePrice(categoryIndex, priceIndex, partial)}
                                                onDelete={() => updateCategory(categoryIndex, {
                                                    prices: (category.prices ?? []).filter((_, index) => index !== priceIndex),
                                                })}
                                            />
                                        ))}
                                        <Button
                                            variant="outlined"
                                            color="breakout"
                                            startIcon={<Add />}
                                            onClick={() => updateCategory(categoryIndex, {
                                                prices: [
                                                    ...(category.prices ?? []),
                                                    {
                                                        title: '',
                                                        description: null,
                                                        price: 0,
                                                        unit: Enum_Componentpricingpricetag_Unit.Eur,
                                                    },
                                                ],
                                            })}
                                        >
                                            Preis hinzufügen
                                        </Button>
                                    </Stack>

                                    <Box>
                                        <Typography variant="overline">Weiterführende Links</Typography>
                                        <LinkEditor<LinkEditable[]>
                                            multi
                                            value={(category.links ?? []) as LinkEditable[]}
                                            onChange={(links) => updateCategory(categoryIndex, { links: links ?? [] })}
                                        />
                                    </Box>
                                </Stack>
                            }
                            image={
                                <>
                                    <MediaView
                                        src={category.image}
                                        fill
                                        slots={{ image: { sizes: '(min-width: 900px) 50vw, 100vw' } }}
                                    />
                                    <EditMediaButtonOverlay
                                        align={categoryIndex % 2 === 0 ? 'right' : 'left'}
                                        onImageChange={(image: MediaRef | null) => {
                                            if (image) updateCategory(categoryIndex, { image });
                                        }}
                                    />
                                </>
                            }
                        />
                    );
                })}
            relatedInfos={
                <RelatedInfosView relatedInfos={editable.relatedInfos as RelatedInfo[]} />
            }
        />
    );
}

function PriceEditorRow({
    price,
    onChange,
    onDelete,
}: {
    price: PricingPriceEditable;
    onChange: (partial: Partial<PricingPriceEditable>) => void;
    onDelete: () => void;
}) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0, 2fr) minmax(7rem, 1fr) 6rem auto' },
                gap: 1.5,
                alignItems: 'start',
            }}
        >
            <TextField
                label="Bezeichnung"
                value={price.title}
                onChange={(event) => onChange({ title: event.target.value })}
                required
                sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
            />
            <TextField
                label="Preis"
                type="number"
                value={price.price}
                onChange={(event) => onChange({ price: Number(event.target.value) })}
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
            />
            <TextField
                select
                label="Währung"
                value={price.unit}
                onChange={(event) => onChange({
                    unit: event.target.value as Enum_Componentpricingpricetag_Unit,
                })}
            >
                {Object.values(Enum_Componentpricingpricetag_Unit).map((unit) => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                ))}
            </TextField>
            <Tooltip title="Preis löschen">
                <IconButton aria-label="Preis löschen" color="error" onClick={onDelete}>
                    <Delete />
                </IconButton>
            </Tooltip>
            <TextField
                label="Zusatzinformation"
                value={price.description ?? ''}
                onChange={(event) => onChange({ description: event.target.value || null })}
                multiline
                sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
            />
        </Box>
    );
}
